import React from 'react'
import { FormattedMessage, IntlShape } from 'react-intl'
import { List as ListIcon } from '@urbaninfrastructure/react-icons'
import {
  Anchor,
  Box,
  Flex,
  H2,
  Li,
  List,
  LoadableButton,
  Text,
} from '../../../components'
import { SettingsQuery } from '../../../lib/core'
import { ApolloError } from 'apollo-client'
import { useFormikContext } from 'formik'
import { Values } from '../types'
import { labelMessages } from '../messages'
import { isInurbaRouen } from '../../../lib/util'
import { useSystemConfig } from '../../../lib/use-system-config'

function SummaryItem({
  label,
  children,
}: {
  label: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Li mb={2} display="flex" justifyContent="center">
      <Text mr={1} bold>
        {label}
      </Text>
      <Box>{children}</Box>
    </Li>
  )
}

const SummaryStep = ({
  intl,
  error,
  loading,
}: {
  intl: IntlShape
  loading: boolean
  error?: ApolloError
}) => {
  const { values } = useFormikContext<Values>()
  const systemConfig = useSystemConfig()
  const inurbaRouen = isInurbaRouen(systemConfig)

  return (
    <>
      <Flex
        flex={1}
        flexWrap="wrap"
        mb="sm"
        textAlign="center"
        justifyContent="center"
        column
      >
        <Box mb="sm">
          <ListIcon size="80px" color="primary" />
          <H2 mb={0} typoStyle="md" mt="sm">
            <FormattedMessage id="summary" defaultMessage="Summary" />
          </H2>
        </Box>
        <Box mb={2}>
          <List>
            <SummaryItem
              label={<FormattedMessage {...labelMessages.administratorName} />}
            >
              {values.administratorName}
            </SummaryItem>
            <SummaryItem
              label={<FormattedMessage {...labelMessages.administratorEmail} />}
            >
              {values.administratorEmail}
            </SummaryItem>
            <SummaryItem
              label={<FormattedMessage {...labelMessages.companyName} />}
            >
              {values.companyName}
            </SummaryItem>
            <SummaryItem
              label={<FormattedMessage {...labelMessages.companyOrgNr} />}
            >
              {values.companyOrgNr}
            </SummaryItem>
            <SummaryItem
              label={<FormattedMessage {...labelMessages.receiptEmail} />}
            >
              {values.receiptEmail}
            </SummaryItem>
            <SummaryItem
              label={<FormattedMessage {...labelMessages.address} />}
            >
              {values.address}
            </SummaryItem>
            <SummaryItem
              label={<FormattedMessage {...labelMessages.zipCode} />}
            >
              {values.zipCode}
            </SummaryItem>
            <SummaryItem label={<FormattedMessage {...labelMessages.city} />}>
              {values.city}
            </SummaryItem>
          </List>
        </Box>
        <Box maxWidth="400px" mx="auto" mb="sm">
          {inurbaRouen ? (
            <FormattedMessage
              id="validate_payment_information"
              defaultMessage="Once you have created an account, you can confirm your payment method."
            />
          ) : (
            <FormattedMessage
              id="register_payment_information"
              defaultMessage="After creating an account you can add a payment method."
            />
          )}
        </Box>
        <Flex justifyContent="center" mb="xs">
          <LoadableButton
            type="submit"
            shape="cool"
            variant="primary"
            loading={loading}
            responseState={error ? 'error' : 'success'}
          >
            <FormattedMessage
              id="create_account"
              defaultMessage="Create account"
            />
          </LoadableButton>
        </Flex>
        <SettingsQuery>
          {({ data }) => {
            if (!data || !data.haveSanityBusinessPage) {
              return null
            }

            const { system } = data
            if (!system || !system.webUrl) {
              return null
            }

            const termsOfUsePath =
              intl.locale === 'en' ? 'en/terms-of-use' : 'vilkaar-for-bruk'
            const termsOfUseUrl = `${system.webUrl}/${termsOfUsePath}`

            return (
              <Box maxWidth="400px" mx="auto">
                <Text typoStyle="xxs" mb="xs" mute>
                  <FormattedMessage
                    id="register_payment_method_terms"
                    defaultMessage="When creating an account, you will accept our terms and conditions. You can read more about our terms of service <link>here</link>."
                    values={{
                      // eslint-disable-next-line react/display-name
                      link: (...chunks) => (
                        <Anchor href={termsOfUseUrl} target="_blank">
                          {chunks}
                        </Anchor>
                      ),
                    }}
                  />
                </Text>
              </Box>
            )
          }}
        </SettingsQuery>
      </Flex>
    </>
  )
}

export default SummaryStep
